import React, { useState, useEffect } from "react";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { GridSortModel, GridFilterModel } from "@mui/x-data-grid";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField} from "@mui/material";
import axiosExt from '../axios';
import { MessageTemplate } from "../types";
import { API_BASE_URL } from "../config.ts";
import { useTranslation } from "react-i18next";

const MessageTemplateTable: React.FC = () => {
    const { t } = useTranslation();

    const [messageTemplates, setMessageTemplates] = useState<MessageTemplate[]>([]);
    const [open, setOpen] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [selectedMessageTemplate, setSelectedMessageTemplate] = useState<MessageTemplate | null>(null);
    const [messageTemplateToDelete, setMessageTemplateToDelete] = useState<string | null>(null);

    const [sortModel, setSortModel] = useState<GridSortModel>([]);
    const [filterModel, setFilterModel] = useState < GridFilterModel>({ items: [] });

    const savedSortKey = "sortMessageTemplateList";
    const savedFilterKey = "filterMessageTemplateList";
    //const [loading, setLoading] = useState(false);

    // Get user roles from localStorage
    const roles: string[] = JSON.parse(localStorage.getItem("roles") || "[]");
    const isAdmin = roles.includes("admin");
    //const isSupport = roles.includes("support");

    useEffect(() => {
        const savedSortModel = JSON.parse(localStorage.getItem(savedSortKey) ?? "[]");
        const savedFilterModel = (() => {
            try {
                const parsed = JSON.parse(localStorage.getItem(savedFilterKey) ?? "{}");
                return parsed && parsed.items ? parsed : { items: [] };
            } catch (error) {
                console.error("Error parsing savedFilterKey:", error);
                return { items: [] }; // Fallback
            }
        })();
        setSortModel(savedSortModel);
        setFilterModel(savedFilterModel);

        console.log("Fetching messageTemplates...");
        fetchMessageTemplates();
    }, []);

    // Save sortModel changes to localStorage
    const handleSortModelChange = (newModel: GridSortModel) => {
        setSortModel(newModel);
        localStorage.setItem(savedSortKey, JSON.stringify(newModel));
    };

    // Save filterModel changes to localStorage
    const handleFilterModelChange = (newModel: GridFilterModel) => {
        setFilterModel(newModel);
        localStorage.setItem(savedFilterKey, JSON.stringify(newModel));
    };

    const fetchMessageTemplates = async () => {
        //setLoading(true);

        try {
            const token = localStorage.getItem("token");
            /*const response = await axiosExt.get<MessageTemplate[]>(`${API_BASE_URL}/messageTemplates`);
            setMessageTemplates(response.data);*/

            const response = await fetch(`${API_BASE_URL}/MessageTemplates`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.status === 401) {
                throw new Error("Unauthorized. Please log in again.");
            }

            const data = await response.json();

           setMessageTemplates(data);

        } catch (error) {
            console.error("Error fetching messageTemplates:", error);
        } finally {
            //setLoading(false);
        }
    };

    const handleEdit = (messageTemplate: MessageTemplate) => {
        setSelectedMessageTemplate(messageTemplate);
        setOpen(true);
    };

    const handleDeleteClick = async (id: string) => {
        if (!isAdmin) {
            alert("Only admin users can delete messageTemplates.");
            return;
        }

        try {
            setMessageTemplateToDelete(id);
            setConfirmDeleteOpen(true);
        } catch (error) {
            console.error("Error deleting messageTemplate:", error);
        }
    };

    const handleConfirmDelete = async () => {
        if (!isAdmin || messageTemplateToDelete === null) return;
        try {
            const token = localStorage.getItem("token");
            await axiosExt.delete(`${API_BASE_URL}/MessageTemplates/${messageTemplateToDelete}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setMessageTemplates(messageTemplates.filter((messageTemplate) => messageTemplate.id !== messageTemplateToDelete));
        } catch (error) {
            console.error("Error deleting messageTemplate:", error);
        } finally {
            setConfirmDeleteOpen(false);
            setMessageTemplateToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setConfirmDeleteOpen(false);
        setMessageTemplateToDelete(null);
    };

    const handleSave = async () => {
        const token = localStorage.getItem("token");
        try {
            if (!selectedMessageTemplate) {
                console.error("Selected messageTemplate is null or undefined.");
                return;
            }

            if (!selectedMessageTemplate.name.trim()) {
                alert("Name cannot be empty.");
                return;
            }
            if (!selectedMessageTemplate.message.trim()) {
                alert("Message cannot be empty.");
                return;
            }

            if (selectedMessageTemplate.id) {
                // Edit MessageTemplate
                await axiosExt.put(`${API_BASE_URL}/MessageTemplates/${selectedMessageTemplate.id}`, selectedMessageTemplate, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
            } else {
                const payload: MessageTemplate = {
                    ...selectedMessageTemplate
                };
                delete payload.id; // Remove the id field

                // Create MessageTemplate
                await axiosExt.post(`${API_BASE_URL}/MessageTemplates`, payload, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
            }
            fetchMessageTemplates();
            handleClose();
        } catch (error) {
            if (error instanceof Error) {
                alert(error.message); // Access the `message` property safely
            } else {
                alert("An unexpected error occurred."); // Handle non-standard errors
            };
        }


        /*if (selectedMessageTemplate) {
            try {
                if (selectedMessageTemplate.id) {
                    // Update existing messageTemplate
                    await axiosExt.put(`${API_BASE_URL}/messageTemplates/${selectedMessageTemplate.id}`, selectedMessageTemplate);
                    setMessageTemplates(
                        messageTemplates.map((c) => (c.id === selectedMessageTemplate.id ? selectedMessageTemplate : c))
                    );
                } else {
                    // Create new messageTemplate
                    const response = await axiosExt.post(`${API_BASE_URL}/messageTemplates`, selectedMessageTemplate);
                    setMessageTemplates([...messageTemplates, response.data]);
                }
                setOpen(false);
                setSelectedMessageTemplate(null);
            } catch (error) {
                console.error("Error saving messageTemplate:", error);
            }
        }*/
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedMessageTemplate(null);
    };

    const columns: GridColDef[] = [
        { field: "name", headerName: t("Name"), width: 150 },
        { field: "message", headerName: t("Message"), width: 200 },
        { field: "subject", headerName: t("Subject"), width: 100 },
        { field: "language", headerName: t("Language"), width: 150 },
        {
            field: "actions",
            headerName: t("Actions"),
            width: 400,
            renderCell: (params: GridRenderCellParams) => (
                <>

                    <Button
                        variant="contained"
                        onClick={() => handleEdit(params.row as MessageTemplate)}
                        style={{ marginRight: "10px" }}
                    >
                        {t("Edit")}
                    </Button>

                    {isAdmin && (
                        <Button variant="contained" color="error" onClick={() => handleDeleteClick(params.row.id)} style={{ marginRight: "10px" }}>{t("Delete")}</Button>
                    )}
                </>
            ),
        },
    ];

    return (
        <div style={{ height: "100%" }}>
            <div style={{ width: "100%", display:"flex", marginTop:"0px"}}>
                <h2>{t("All MessageTemplates")}</h2>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        setSelectedMessageTemplate({ id: "", name: "", message: "", subject: "", language:""});
                        setOpen(true);
                    }}
                    style={{ marginBottom: "20px", marginTop:"20px", marginLeft:"20px" }}
                >
                    {t("Add New")}
                </Button>
            </div>
            <div style={{ width: "100%" }}>
                <DataGrid rows={messageTemplates} columns={columns}
                    sortModel={sortModel}
                    onSortModelChange={handleSortModelChange}
                    filterModel={filterModel}
                    onFilterModelChange={handleFilterModelChange}
                    initialState={{
                        pagination: {
                            paginationModel: { pageSize: 25, page: 0 },
                        },
                    }}
                    pageSizeOptions={[25, 50, 100, 200]} // Optional: Specify page size options
                />
                <Dialog open={confirmDeleteOpen} onClose={handleCancelDelete}>
                    <DialogTitle>{t("Confirm Deletion")}</DialogTitle>
                    <DialogContent>{t("Are you sure you want to delete this record?")}</DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelDelete} color="secondary">{t("no")}</Button>
                        <Button onClick={handleConfirmDelete} color="error">{t("yes")}</Button>
                    </DialogActions>
                </Dialog>
            </div>

            {/* Dialog for Viewing/Editing MessageTemplate */}
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>{selectedMessageTemplate?.id ? t("Edit") : t("Add") }</DialogTitle>
                <DialogContent>
                    <TextField
                        margin="dense"
                        required
                        label={t("Name")}
                        fullWidth
                        value={selectedMessageTemplate?.name || ""}
                        onChange={(e) =>
                            setSelectedMessageTemplate((prev) => prev && { ...prev, name: e.target.value })
                        }
                    />
                    
                    <TextField
                        margin="dense"
                        label={t("Subject")}
                        fullWidth
                        value={selectedMessageTemplate?.subject || ""}
                        onChange={(e) =>
                            setSelectedMessageTemplate((prev) => prev && { ...prev, subject: e.target.value })
                        }
                    />

                    <TextField
                        margin="dense"
                        label={t("Message")}
                        type="string"
                        multiline
                        required
                        fullWidth
                        value={selectedMessageTemplate?.message || ""}
                        onChange={(e) =>
                            setSelectedMessageTemplate((prev) =>
                                prev ? { ...prev, message: e.target.value } : null
                            )
                        }
                    />
                    <TextField
                        margin="dense"
                        label={t("Language")}
                        fullWidth
                        multiline
                        value={selectedMessageTemplate?.language || ""}
                        onChange={(e) =>
                            setSelectedMessageTemplate((prev) => prev && { ...prev, language: e.target.value })
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="secondary">
                        {t("Cancel")}
                    </Button>
                    <Button onClick={handleSave} color="primary">
                        {t("Save")}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default MessageTemplateTable;
